import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LetterInsights } from "@/components/letters/LetterInsights";

const sel = "min-h-[40px] rounded-lg border border-slate-200 bg-white px-2 text-sm";

function ReviewForm({ t, onDone }) {
  const [f, setF] = useState({ status: t.status, enabled: !!t.enabled, review_note: t.review_note || "", effective_date: t.effective_date || "" });
  const save = async () => {
    try { await api.post(`/admin/letter-templates/${t.id}/review`, { ...f, effective_date: f.effective_date || null }); toast.success("Saved"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="mt-3 grid gap-2 sm:grid-cols-4">
      <select className={sel} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid={`admin-lib-status-${t.id}`}>
        {["draft", "pending_review", "approved", "retired"].map((s) => <option key={s} value={s}>{s === "approved" ? "Approved for Production" : s.replace("_", " ")}</option>)}
      </select>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} data-testid={`admin-lib-enabled-${t.id}`} />Enabled</label>
      <Input type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} />
      <Input placeholder="Attorney/compliance review note" value={f.review_note} onChange={(e) => setF({ ...f, review_note: e.target.value })} data-testid={`admin-lib-note-${t.id}`} />
      <Button size="sm" onClick={save} className="sm:col-span-4 sm:w-40 rounded-lg" data-testid={`admin-lib-save-${t.id}`}>Save review</Button>
    </div>
  );
}

function VersionForm({ t, onDone }) {
  const [txt, setTxt] = useState(JSON.stringify({ authorities: t.authorities, structures: t.structures, clauses: t.clauses, required_evidence: t.required_evidence }, null, 2));
  const save = async () => {
    try { await api.post(`/admin/letter-templates/${t.letter_key}/versions`, { changes: JSON.parse(txt) }); toast.success("New draft version created"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Invalid changes (JSON)"); }
  };
  return (
    <div className="mt-3 space-y-2">
      <Textarea rows={8} className="font-mono text-xs" value={txt} onChange={(e) => setTxt(e.target.value)} data-testid={`admin-lib-version-json-${t.letter_key}`} />
      <Button size="sm" variant="outline" onClick={save} className="rounded-lg" data-testid={`admin-lib-version-save-${t.letter_key}`}>Create new draft version</Button>
    </div>
  );
}

function TemplateRow({ versions, onDone }) {
  const [open, setOpen] = useState(false);
  const latest = versions[versions.length - 1];
  const live = versions.filter((v) => v.status === "approved" && v.enabled).pop();
  return (
    <li className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-lib-row-${latest.letter_key}`}>
      <button type="button" className="w-full text-left" onClick={() => setOpen(!open)}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-semibold text-slate-900">{latest.name}</span>
          <span className="text-xs text-slate-500">Round {String(latest.round)} · {latest.recipient_type} · {versions.length} version(s) · {live ? `Live: v${live.version}` : "Not live"}</span>
        </div>
      </button>
      {open && versions.map((v) => (
        <div key={v.id} className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-700">
          <div className="font-semibold">v{v.version} · {v.status} · {v.enabled ? "enabled" : "disabled"} {v.source && v.source !== "library" ? `· ${v.source}` : ""}</div>
          <div>Rule: {v.rule} · Required: {v.required_fields.join(", ")} · Evidence: {(v.required_evidence || []).join(", ") || "none"}</div>
          <div>Authorities: {v.authorities.join("; ") || "none"} · Structures: {v.structures.join(", ")}</div>
          <details className="mt-1"><summary className="cursor-pointer">Change history ({v.history?.length || 0})</summary>
            <ul>{(v.history || []).map((h, i) => <li key={i}>{h.at} · {h.action} {h.status || ""} {h.note ? `· ${h.note}` : ""}</li>)}</ul></details>
          <ReviewForm t={v} onDone={onDone} />
        </div>
      ))}
      {open && <VersionForm t={latest} onDone={onDone} />}
    </li>
  );
}

function LegacySection({ legacy, onDone }) {
  const act = async (lt, path, body) => {
    try { await api.post(`/admin/legacy-templates/${lt}/${path}`, body); toast.success("Done"); onDone(); } catch { toast.error("Failed"); }
  };
  return (
    <ul className="mt-3 grid gap-2">
      {legacy.map((l) => (
        <li key={l.letter_type} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3 text-sm" data-testid={`admin-legacy-${l.letter_type}`}>
          <span><span className="text-xs font-semibold rounded-full bg-slate-100 px-2 py-0.5 mr-2">{l.label}</span>{l.title}{l.retired ? " · Retired" : ""}</span>
          <span className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => act(l.letter_type, "retire", { retired: !l.retired })} data-testid={`admin-legacy-retire-${l.letter_type}`}>{l.retired ? "Restore" : "Retire"}</Button>
            <Button size="sm" variant="outline" onClick={() => act(l.letter_type, "convert")} data-testid={`admin-legacy-convert-${l.letter_type}`}>Convert to library draft</Button>
          </span>
        </li>
      ))}
    </ul>
  );
}

function OverrideForm() {
  const [f, setF] = useState({ user_id: "", letter_key: "mov", reason: "" });
  const save = async () => {
    try { await api.post("/admin/letter-library/overrides", f); toast.success("Override recorded"); setF({ ...f, reason: "" }); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="mt-3 grid gap-2 sm:grid-cols-4">
      <Input placeholder="Member user ID" value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} data-testid="admin-override-user" />
      <Input placeholder="Letter key" value={f.letter_key} onChange={(e) => setF({ ...f, letter_key: e.target.value })} data-testid="admin-override-key" />
      <Input placeholder="Reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="admin-override-reason" />
      <Button size="sm" onClick={save} data-testid="admin-override-save">Record override</Button>
    </div>
  );
}

export default function AdminLetterLibrary() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/letter-templates").then((r) => setData(r.data)).catch(() => toast.error("Could not load"));
  useEffect(() => { load(); }, []);
  if (!data) return <AppShell><div className="text-slate-500">Loading…</div></AppShell>;
  const byKey = data.templates.reduce((m, t) => ({ ...m, [t.letter_key]: [...(m[t.letter_key] || []), t] }), {});
  return (
    <AppShell>
      <div className="eyebrow">Admin</div>
      <h1 className="text-3xl font-semibold text-slate-900 mt-1" data-testid="admin-lib-heading">Letter Library Manager</h1>
      <p className="mt-2 text-sm text-slate-600">Only versions marked “Approved for Production” and enabled can generate member letters. Earlier versions are kept for auditing.</p>
      <LetterInsights />
      <h2 className="mt-10 text-lg font-semibold text-slate-900">Letter templates</h2>
      <ul className="mt-3 grid gap-2">{Object.values(byKey).map((v) => <TemplateRow key={v[0].letter_key} versions={v} onDone={load} />)}</ul>
      <h2 className="mt-10 text-lg font-semibold text-slate-900">Legacy manual templates</h2>
      <LegacySection legacy={data.legacy} onDone={load} />
      <h2 className="mt-10 text-lg font-semibold text-slate-900">Eligibility override</h2>
      <OverrideForm />
    </AppShell>
  );
}
